import { useState, useCallback } from "react";
import * as api from "../api";

// Gestor de proyectos nativo. Mismo patron que useVacaciones: un useCallback por
// carga que depende solo de `token`, cada mutacion envuelta en try/catch con
// showToast, y las listas actualizadas comparando con String(a) === String(b).
//
// El permiso de escritura NO se decide aqui: llega en cada fila como `can_edit`,
// resuelto por backend/lib/permisos.js. El espejo de src/utils.js solo sirve
// para no ofrecer botones que el servidor va a rechazar.
export function useProyectos(token, showToast) {
  const [proyectos, setProyectos] = useState([]);
  const [cargando,  setCargando ] = useState(false);
  // detalle = { proyecto, miembros, tareas, dependencias, historial }. Llega de
  // una sola peticion: la vista de flujo necesita el grafo entero de golpe, y
  // asi no hay dos formas distintas de cargarlo.
  const [detalle,   setDetalle  ] = useState(null);
  const [historial, setHistorial] = useState([]);
  // Los protocolos van en su propia lista y no mezclados con los proyectos: el
  // backend ya los separa, y tenerlos aparte evita que un filtro o un indicador
  // de la pagina los cuente por descuido.
  const [plantillas, setPlantillas] = useState([]);

  const loadProyectos = useCallback(async (filtros) => {
    if (!token) return;
    setCargando(true);
    try {
      const data = await api.getProyectos(filtros);
      if (Array.isArray(data)) setProyectos(data);
    } catch (e) {
      showToast(e.message || "Error al cargar los proyectos", "error");
    } finally {
      setCargando(false);
    }
  }, [token]); // eslint-disable-line

  const loadProyecto = useCallback(async (id) => {
    if (!token || !id) return null;
    setCargando(true);
    try {
      const d = await api.getProyecto(id);
      setDetalle(d);
      return d;
    } catch (e) {
      showToast(e.message || "Error al abrir el proyecto", "error");
      return null;
    } finally {
      setCargando(false);
    }
  }, [token]); // eslint-disable-line

  const loadHistorialReciente = useCallback(async (limit = 15) => {
    if (!token) return;
    try {
      const h = await api.getHistorialReciente(limit);
      if (Array.isArray(h)) setHistorial(h);
    } catch (e) {
      // El historial es un adorno del panel: si falla, no se avisa con un toast
      // porque no impide trabajar. Se deja rastro en consola y ya.
      console.warn("[proyectos] no se pudo leer el historial reciente:", e.message);
    }
  }, [token]); // eslint-disable-line

  // Refleja en la lista de tarjetas una fila que el servidor ya ha devuelto
  // entera. No vuelve a escribir.
  const reemplazarProyecto = (p) =>
    setProyectos(prev => prev.map(x => String(x.id) === String(p.id) ? { ...x, ...p } : x));

  // ── Protocolos ─────────────────────────────────────────────────────────────
  const loadPlantillas = useCallback(async () => {
    if (!token) return;
    try {
      const d = await api.getPlantillas();
      if (Array.isArray(d)) setPlantillas(d);
    } catch (e) {
      showToast(e.message || "Error al cargar los protocolos", "error");
    }
  }, [token]); // eslint-disable-line

  const guardarComoPlantilla = async (id, d) => {
    try {
      const p = await api.guardarComoPlantilla(id, d);
      setPlantillas(prev => [p, ...prev]);
      showToast(`Protocolo creado con ${p.pasos} paso(s)`);
      return p;
    } catch (e) { showToast(e.message || "Error al crear el protocolo", "error"); return null; }
  };

  // Nace un proyecto de verdad, asi que entra en la lista de proyectos y no en
  // la de protocolos.
  const instanciarPlantilla = async (id, d) => {
    try {
      const p = await api.instanciarPlantilla(id, d);
      setProyectos(prev => [p, ...prev]);
      showToast(`Proyecto creado con ${p.pasos} paso(s)`);
      return p;
    } catch (e) { showToast(e.message || "Error al crear el proyecto", "error"); return null; }
  };

  const createProyecto = async (d) => {
    try {
      const p = await api.createProyecto(d);
      setProyectos(prev => [p, ...prev]);
      showToast("Proyecto creado");
      return p;
    } catch (e) { showToast(e.message || "Error al crear el proyecto", "error"); return null; }
  };

  const updateProyecto = async (id, d) => {
    try {
      const p = await api.updateProyecto(id, d);
      reemplazarProyecto(p);
      setDetalle(prev => prev && String(prev.proyecto?.id) === String(id)
        ? { ...prev, proyecto: { ...prev.proyecto, ...p } } : prev);
      return p;
    } catch (e) { showToast(e.message || "Error al guardar el proyecto", "error"); return null; }
  };

  // Archivar es lo que hace el DELETE normal: el proyecto sale de la lista pero
  // sigue entero, con sus pasos, su historial y las horas que se le imputaron.
  const archivarProyecto = async (id) => {
    try {
      await api.deleteProyecto(id);
      setProyectos(prev => prev.filter(x => String(x.id) !== String(id)));
      showToast("Proyecto archivado. Sale de la lista, pero no se pierde nada", "warn");
      return true;
    } catch (e) { showToast(e.message || "Error al archivar el proyecto", "error"); return false; }
  };

  const desarchivarProyecto = async (id) => {
    try {
      const p = await api.updateProyecto(id, { archived: false });
      reemplazarProyecto(p);
      setDetalle(prev => prev && String(prev.proyecto?.id) === String(id)
        ? { ...prev, proyecto: { ...prev.proyecto, archived: false } } : prev);
      showToast("Proyecto recuperado");
      return p;
    } catch (e) { showToast(e.message || "Error al recuperar el proyecto", "error"); return null; }
  };

  // El borrado de verdad. El servidor exige que este archivado antes, y congela
  // en texto el nombre del proyecto y el de sus pasos en los apuntes del
  // cuaderno: esas horas no se borran nunca y tienen que seguir siendo legibles.
  const borrarProyecto = async (id) => {
    try {
      const r = await api.deleteProyecto(id, true);
      setProyectos(prev => prev.filter(x => String(x.id) !== String(id)));
      showToast(r?.apuntes
        ? `Proyecto borrado. ${r.apuntes} apunte(s) del cuaderno conservan su nombre en texto`
        : "Proyecto borrado");
      return true;
    } catch (e) { showToast(e.message || "Error al borrar el proyecto", "error"); return false; }
  };

  // ── Pasos ───────────────────────────────────────────────────────────────────
  // Todas las mutaciones de paso reflejan en `detalle.tareas` y recalculan los
  // recuentos de la tarjeta, para que la vista general no quede desfasada sin
  // tener que recargar la lista entera.
  const recuentaTarjeta = (projectId, tareas) => {
    const trabajo = tareas.filter(t => (t.kind || "tarea") === "tarea");
    const hoy = new Date();
    const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
    setProyectos(prev => prev.map(p => String(p.id) !== String(projectId) ? p : {
      ...p,
      tareas:      trabajo.length,
      completadas: trabajo.filter(t => t.status === "Completada").length,
      bloqueadas:  trabajo.filter(t => t.status === "Bloqueada").length,
      vencidas:    trabajo.filter(t => t.due_date && t.due_date < hoyIso
                                    && t.status !== "Completada" && t.status !== "Cancelada").length,
    }));
  };

  const conTareas = (fn) => setDetalle(prev => {
    if (!prev) return prev;
    const tareas = fn(prev.tareas || []);
    recuentaTarjeta(prev.proyecto?.id, tareas);
    return { ...prev, tareas };
  });

  const createTarea = async (pid, d) => {
    try {
      const t = await api.createTarea(pid, d);
      conTareas(ts => [...ts, t]);
      return t;
    } catch (e) { showToast(e.message || "Error al crear el paso", "error"); return null; }
  };

  const updateTarea = async (pid, tid, d) => {
    try {
      const t = await api.updateTarea(pid, tid, d);
      conTareas(ts => ts.map(x => String(x.id) === String(tid) ? { ...x, ...t } : x));
      return t;
    } catch (e) { showToast(e.message || "Error al guardar el paso", "error"); return null; }
  };

  const deleteTarea = async (pid, tid) => {
    try {
      await api.deleteTarea(pid, tid);
      setDetalle(prev => {
        if (!prev) return prev;
        const tareas = (prev.tareas || []).filter(x => String(x.id) !== String(tid));
        // Al borrar un paso desaparecen tambien sus aristas (ON DELETE CASCADE
        // en la base de datos): hay que quitarlas aqui o el lienzo dibujaria
        // conexiones a un nodo que ya no existe.
        const dependencias = (prev.dependencias || []).filter(
          d => String(d.from_task_id) !== String(tid) && String(d.to_task_id) !== String(tid));
        recuentaTarjeta(prev.proyecto?.id, tareas);
        return { ...prev, tareas, dependencias };
      });
      showToast("Paso eliminado", "warn");
      return true;
    } catch (e) { showToast(e.message || "Error al eliminar el paso", "error"); return false; }
  };

  const reordenarTareas = async (pid, orden) => {
    try {
      await api.reordenarTareas(pid, orden);
      const pos = Object.fromEntries(orden.map(o => [String(o.id), o.sort_order]));
      conTareas(ts => [...ts]
        .map(t => pos[String(t.id)] !== undefined ? { ...t, sort_order: pos[String(t.id)] } : t)
        .sort((a, b) => (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9)));
      return true;
    } catch (e) { showToast(e.message || "Error al reordenar", "error"); return false; }
  };

  const setAsignaciones = async (pid, tid, d) => {
    try {
      const asignados = await api.setTareaAsignaciones(pid, tid, d);
      conTareas(ts => ts.map(x => String(x.id) === String(tid) ? { ...x, asignados } : x));
      return asignados;
    } catch (e) { showToast(e.message || "Error al asignar el paso", "error"); return null; }
  };

  // ── Aristas del flujo ───────────────────────────────────────────────────────
  const conDeps = (fn) => setDetalle(prev =>
    prev ? { ...prev, dependencias: fn(prev.dependencias || []) } : prev);

  const crearDependencia = async (pid, d) => {
    try {
      const e = await api.createDependencia(pid, d);
      conDeps(ds => [...ds, e]);
      return e;
    } catch (e) {
      // 409 DUPLICADA no es un error del que haya que alarmarse: el editor pudo
      // pedir dos veces la misma conexion. Se avisa suave y no se rompe nada.
      showToast(e.message || "Error al crear la conexion", "error");
      return null;
    }
  };

  const editarDependencia = async (pid, did, d) => {
    try {
      const e = await api.updateDependencia(pid, did, d);
      conDeps(ds => ds.map(x => String(x.id) === String(did) ? { ...x, ...e } : x));
      return e;
    } catch (e) { showToast(e.message || "Error al guardar la conexion", "error"); return null; }
  };

  const borrarDependencia = async (pid, did) => {
    try {
      await api.deleteDependencia(pid, did);
      conDeps(ds => ds.filter(x => String(x.id) !== String(did)));
      return true;
    } catch (e) { showToast(e.message || "Error al eliminar la conexion", "error"); return false; }
  };

  // Posiciones del lienzo, en lote. Devuelve false para que el editor sepa que
  // no puede dar por guardadas esas posiciones y las siga teniendo pendientes.
  const guardarPosiciones = async (pid, nodos) => {
    try {
      await api.guardarPosiciones(pid, nodos);
      // El reflejo local ya lo tiene el editor; aqui solo se sincroniza el
      // detalle para que al cerrar y reabrir el proyecto los nodos no salten.
      setDetalle(prev => {
        if (!prev) return prev;
        const pos = Object.fromEntries(nodos.map(n => [String(n.id), n]));
        return {
          ...prev,
          tareas: (prev.tareas || []).map(t => pos[String(t.id)]
            ? { ...t, pos_x: pos[String(t.id)].pos_x, pos_y: pos[String(t.id)].pos_y } : t),
        };
      });
      return true;
    } catch (e) {
      showToast(e.message || "No se pudo guardar la posicion de los nodos", "error");
      return false;
    }
  };

  // ── Miembros ────────────────────────────────────────────────────────────────
  const addMiembro = async (pid, d) => {
    try {
      const m = await api.addProyectoMiembro(pid, d);
      setDetalle(prev => prev ? { ...prev, miembros: [...(prev.miembros || []).filter(
        x => String(x.user_id) !== String(m.user_id)), m] } : prev);
      return m;
    } catch (e) { showToast(e.message || "Error al añadir a la persona", "error"); return null; }
  };

  const removeMiembro = async (pid, uid) => {
    try {
      await api.removeProyectoMiembro(pid, uid);
      setDetalle(prev => prev ? { ...prev,
        miembros: (prev.miembros || []).filter(x => String(x.user_id) !== String(uid)) } : prev);
      return true;
    } catch (e) { showToast(e.message || "Error al quitar a la persona", "error"); return false; }
  };

  // ── Tiempo real del cuaderno ────────────────────────────────────────────────
  // Devuelve la respuesta entera (no solo las filas) porque trae `parcial`, que
  // dice si el servidor ha recortado el desglose por persona a quien pregunta.
  // Esconderlo seria peor que avisarlo.
  const loadTiempo = useCallback(async (pid, f) => {
    if (!token || !pid) return null;
    try {
      return await api.getProyectoTiempo(pid, f);
    } catch (e) {
      showToast(e.message || "No se pudo leer el tiempo real", "error");
      return null;
    }
  }, [token]); // eslint-disable-line

  return {
    proyectos, setProyectos, cargando, detalle, setDetalle, historial,
    plantillas, loadPlantillas, guardarComoPlantilla, instanciarPlantilla,
    loadProyectos, loadProyecto, loadHistorialReciente,
    createProyecto, updateProyecto, reemplazarProyecto,
    archivarProyecto, desarchivarProyecto, borrarProyecto,
    createTarea, updateTarea, deleteTarea, reordenarTareas, setAsignaciones,
    crearDependencia, editarDependencia, borrarDependencia, guardarPosiciones,
    addMiembro, removeMiembro, loadTiempo,
  };
}
